import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet } from "./client";

/** `GET /api/v2/yorumlar?rating=` — sozlesme §4 (backend/app/core/api/yorumlar.py). */

export const yorumSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    platform_native_id: z.string().nullable().optional(),
    product_native_id: z.union([z.string(), z.number()]).nullable().optional(),
    sku: z.string().nullable().optional(),
    product_name: z.string().nullable().optional(),
    customer_name: z.string().nullable().optional(),
    /** `core.reviews.rating` NOT NULL degil (backend/db/init/100_core_schema.sql) — null gelebilir. */
    rating: z.number().nullable(),
    body: z.string().nullable().optional(),
    is_verified_purchase: z.boolean().nullable().optional(),
    is_approved: z.boolean().nullable().optional(),
    created_at_platform: z.string().nullable().optional(),
    /** adapter.admin_link henuz bu ucta doldurulmuyor (bkz. api/stok.ts ayni not). */
    admin_link: z.string().nullable().optional(),
    /**
     * Görev A (eski arayüzle eşitlik) — `core.reviews` sütunu DEĞİL, backend
     * `raw` JSONB'den okur (bkz. backend/app/core/api/yorumlar.py); raw'da
     * anahtar yoksa (eski/raw'sız satır) null gelir.
     */
    title: z.string().nullable().optional(),
    answer: z.string().nullable().optional(),
    useful: z.number().nullable().optional(),
    not_useful: z.number().nullable().optional(),
    recommended: z.boolean().nullable().optional(),
  })
  .passthrough();
export type Yorum = z.infer<typeof yorumSchema>;

const yorumlarOzetSchema = z
  .object({
    toplam: z.number(),
    ort_puan: z.number().nullable(),
    dusuk_puan: z.number(),
    urun_sayisi: z.number(),
    /** Ticimax'te onay bekleyen (yayında olmayan, puansız) yorum sayısı. */
    onay_bekleyen: z.number(),
    /** Görev A: "Ürünü önerenler %" — raw'da `recommended` alanı olan kayıtlar üzerinden; veri yoksa null. */
    oneren_yuzde: z.number().nullable(),
  })
  .partial()
  .passthrough();
export type YorumlarOzet = z.infer<typeof yorumlarOzetSchema>;

const yorumlarOkSchema = z.object({
  status: z.literal("ok"),
  yorumlar: z.array(yorumSchema),
  ozet: yorumlarOzetSchema,
});

const notSupportedSchema = z.object({ status: z.literal("not_supported") });

export const yorumlarResponseSchema = z.union([yorumlarOkSchema, notSupportedSchema]);
export type YorumlarResponse = z.infer<typeof yorumlarResponseSchema>;

/** "hepsi" = filtresiz (?rating parametresi gonderilmez). */
export type YorumPuanFiltresi = 1 | 2 | 3 | 4 | 5 | "hepsi";

export function fetchYorumlar(rating: YorumPuanFiltresi = "hepsi"): Promise<YorumlarResponse> {
  const qs = rating === "hepsi" ? "" : `?rating=${rating}`;
  return apiGet(`/api/v2/yorumlar${qs}`, yorumlarResponseSchema);
}

export function useYorumlar(rating: YorumPuanFiltresi = "hepsi") {
  return useQuery({
    queryKey: ["yorumlar", rating] as const,
    queryFn: () => fetchYorumlar(rating),
    // INCELEME BULGUSU: `rating` sorgu anahtarinin PARCASI oldugundan filtre
    // degisince onceden query "pending" durumuna donup Yorumlar.tsx TAM
    // SAYFA "Yükleniyor…" mesajina duserdi (baslik/istatistik/filtre
    // sekmesi dahil sayfa iskeleti kaybolurdu). `keepPreviousData` onceki
    // filtrenin verisini yeni istek sonuclanana kadar KORUR — sayfa
    // iskeleti kalir, `isPlaceholderData` yalniz LISTE alaninda kisa bir
    // yukleniyor durumu icin kullanilir (bkz. Yorumlar.tsx).
    placeholderData: keepPreviousData,
  });
}
